<template>
<div>
    <ImportPreviewTable
        :preview-data="previewData"
        :title="title"
        :show-summary="showSummary"
        :summary-cards="contactSummaryCards"
        :custom-columns="contactCustomColumns"
        :table-headers="contactTableHeaders"
        :items-per-page="itemsPerPage"
        :hide-footer="hideFooter"
        :loading="loading"
        :auto-filter-to-errors="autoFilterToErrors"
        :table-height="tableHeight"
        :selected-row-numbers="selectedRows.map(r => r.row_number)"
        :show-selection-filter="true"
    >

        <template #column-select="{ item }">
            <v-checkbox
                :input-value="isRowSelected(item)"
                :disabled="!canRowBeSelected(item)"
                @change="toggleRowSelection(item)"
                hide-details
                dense
                :class="{ 'disabled-checkbox': !canRowBeSelected(item) }"
            ></v-checkbox>
        </template>

        <template #header-select>
            <div class="d-flex align-center">
                <v-checkbox
                    :input-value="selectAll"
                    :indeterminate="isIndeterminate"
                    @change="toggleSelectAll"
                    hide-details
                    dense
                    class="mr-2"
                ></v-checkbox>
            </div>
        </template>

        <!-- List Membership Status Column (for contact list imports) -->
        <template #column-rc_add_to_membership_status="{ value }">
            <v-chip
                v-if="value"
                :color="getListMembershipStatusColor(value)"
                text-color="white"
                small
            >
                {{ formatListMembershipStatus(value) }}
            </v-chip>
            <span v-else class="text--disabled">-</span>
        </template>

        <!-- Bulk Message Recipient Status Column (for bulk message recipient imports) -->
        <template #column-rc_bulk_message_recipient_status="{ value }">
            <v-chip
                v-if="value"
                :color="getListMembershipStatusColor(value)"
                text-color="white"
                small
            >
                {{ formatListMembershipStatus(value) }}
            </v-chip>
            <span v-else class="text--disabled">-</span>
        </template>

        <!-- Extra Fields Column - shows a button if populated -->
        <template #column-rc_extra_fields="{ value, item }">
            <v-btn
                v-if="value && value.trim()"
                x-small
                outlined
                color="primary"
                @click="openExtraFieldsDialog(item)"
            >
                <v-icon x-small left>mdi-eye</v-icon>
                View
            </v-btn>
            <span v-else class="text--disabled">-</span>
        </template>

    </ImportPreviewTable>

    <!-- Extra Fields Dialog -->
    <v-dialog v-model="extra_fields_dialog" max-width="500">
        <v-card>
            <v-card-title class="headline">
                <v-icon left color="primary">mdi-form-textbox</v-icon>
                Custom Fields
            </v-card-title>
            <v-card-text>
                <v-simple-table v-if="parsed_extra_fields && Object.keys(parsed_extra_fields).length > 0">
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th class="text-left">Column Name</th>
                                <th class="text-left">Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(fieldValue, fieldName) in parsed_extra_fields" :key="fieldName">
                                <td><strong>{{ fieldName }}</strong></td>
                                <td>{{ fieldValue }}</td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
                <div v-else class="text-center grey--text pa-4">
                    No custom fields available
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="extra_fields_dialog = false">Close</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</div>
</template>



<script>
import ImportPreviewTable from '@/components/common/import-preview-table.vue';

export default {
    name: 'contact-import-preview-table',

    components: {
        ImportPreviewTable
    },

    props: {
        previewData: {
            type: Object,
            default: null
        },
        title: {
            type: String,
            default: ''
        },
        showSummary: {
            type: Boolean,
            default: true
        },
        summaryCards: {
            type: Array,
            default: () => []
        },
        customColumns: {
            type: Array,
            default: () => []
        },
        tableHeaders: {
            type: Array,
            default: () => []
        },
        itemsPerPage: {
            type: Number,
            default: 25
        },
        hideFooter: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        autoFilterToErrors: {
            type: Boolean,
            default: false
        },
        selectedRows: {
            type: Array,
            default: () => []
        },
        selectAll: {
            type: Boolean,
            default: false
        },
    },


    computed: {
        contactSummaryCards() {
            return this.summaryCards.length > 0 ? this.summaryCards : [
                { key: 'total_records', label: 'Total Records', cols: 3 },
                { key: 'valid_records', label: 'Valid Records', cols: 3 },
            ];
        },

        contactCustomColumns() {
            return this.customColumns;
        },

        contactTableHeaders() {
            return this.tableHeaders;
        },

        isIndeterminate() {
            if (!this.previewData || !this.previewData.preview_rows) {
                return false;
            }
            // Only consider rows that can be imported for indeterminate state
            const importableRows = this.previewData.preview_rows.filter(row => row.will_be_imported);
            return this.selectedRows.length > 0 && this.selectedRows.length < importableRows.length;
        }
    },

    data() {
        return {
            dense: true,
            tableHeight: 300,
            extra_fields_dialog: false,
            parsed_extra_fields: null
        };
    },

    mounted() {
 
    },

    methods: {

        isRowSelected(item) {
            return this.selectedRows.some(row => row.row_number === item.row_number);
        },

        canRowBeSelected(item) {
            // A row can be selected if:
            // 1. It's explicitly marked as will_be_imported = true, OR
            // 2. It doesn't have validation errors (even if will_be_imported is not set yet)
            if (item.will_be_imported === true) {
                return true;
            }
            if (item.will_be_imported === false) {
                return false;
            }
            // If will_be_imported is not set, check for validation errors
            return !this.hasValidationErrors(item);
        },

        toggleRowSelection(item) {
            // Only allow selection if the row can be selected
            if (this.canRowBeSelected(item)) {
                this.$emit('toggle-row-selection', item);
            }
        },

        toggleSelectAll() {
            this.$emit('toggle-select-all');
        },



        hasValidationErrors(item) {
            // Check if the item has validation errors that would prevent inclusion
            return (item.errors && item.errors.length > 0) ||
                   (item.has_errors === true || item.has_errors === 'true') ||
                   (item.validation_errors && item.validation_errors.trim() !== '');
        },

        // List membership status formatting methods (for contact list imports)
        formatListMembershipStatus(status) {
            if (!status) return '-';

            // Use enum utility, get the pretty text
            const prettyStatus = this.$rc.pretty(status, 'enum_add_to_membership_status');
            if (prettyStatus) return prettyStatus;

            // Fallback to raw value if enum utility is not available
            return status;

        },

        getListMembershipStatusColor(status) {
            if (!status) return 'grey';

            switch (status) {
                // Add mode statuses
                case 'existing-member':
                    return 'info';
                case 'can-be-added':
                    return 'success';
                case 'cannot-be-added':
                    return 'error';
                // Remove mode statuses
                case 'can-be-removed':
                    return 'success';
                case 'not-a-member':
                    return 'grey';
                default:
                    return 'grey';
            }
        },

        getListMembershipStatusIcon(status) {
            if (!status) return 'mdi-minus';

            switch (status) {
                case 'existing-member':
                    return 'mdi-account-check';
                case 'will-be-added':
                    return 'mdi-account-plus';
                case 'will-not-be-added':
                    return 'mdi-account-remove';
                default:
                    return 'mdi-help';
            }
        },

        openExtraFieldsDialog(item) {
            try {
                // Parse the JSON string
                const extraFieldsStr = item.rc_extra_fields;
                if (extraFieldsStr && extraFieldsStr.trim()) {
                    this.parsed_extra_fields = JSON.parse(extraFieldsStr);
                } else {
                    this.parsed_extra_fields = null;
                }
            } catch (e) {
                // If parsing fails, show the raw value
                this.parsed_extra_fields = { 'Raw Data': item.rc_extra_fields };
            }
            this.extra_fields_dialog = true;
        }
    }
};
</script>

<style scoped>
.v-data-table {
    border-radius: 8px;
}

.v-chip {
    font-size: 11px;
}

.disabled-checkbox {
    opacity: 0.6;
    pointer-events: none;
}

.disabled-checkbox .v-input--selection-controls__input {
    background-color: #f5f5f5 !important;
    border: 1px solid #d0d0d0 !important;
}

/* List membership status chip styling */
.list-status-chip {
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.list-status-chip .v-icon {
    font-size: 14px !important;
}

.disabled-checkbox .v-input--selection-controls__ripple {
    display: none !important;
}

.disabled-checkbox .v-icon {
    color: #999999 !important;
}

/* Show an X icon for disabled checkboxes to make it clear they cannot be selected */
.disabled-checkbox .v-input--selection-controls__input::after {
    content: '✕';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #cc0000;
    font-size: 12px;
    font-weight: bold;
}
</style>
